import DICT from "./domDict.json";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = "script,style,code,pre,textarea,[data-no-i18n],[contenteditable=true]";
const orig = new WeakMap();
const attrOrig = new WeakMap();
const norm = (s) => s.replace(/\s+/g, " ").trim();

const lookup = (text, lang) => {
  const core = norm(text);
  if (!core) return null;
  const hit = DICT[core];
  if (hit) return hit[lang];
  for (const sep of [" — ", ": "]) {
    const i = core.indexOf(sep);
    if (i > 0) {
      const head = DICT[core.slice(0, i)] || DICT[core.slice(0, i + sep.trimEnd().length)];
      if (head) {
        const h = DICT[core.slice(0, i)] ? head[lang] : head[lang].replace(/[:—]\s*$/, "");
        return h + core.slice(i);
      }
    }
  }
  return null;
};

const wrap = (text, t) => {
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  return lead + t + trail;
};

const skipped = (el) => !el || (el.closest && el.closest(SKIP));
// Flash Tools pins its UI to one language via data-i18n-lock; notifications opt back in with data-i18n-free.
const effLang = (el, lang) => {
  const lock = el && el.closest && el.closest("[data-i18n-lock]");
  if (!lock) return lang;
  const free = el.closest("[data-i18n-free]");
  return free && lock.contains(free) ? lang : lock.getAttribute("data-i18n-lock");
};

function doText(node, lang) {
  if (skipped(node.parentElement)) return;
  lang = effLang(node.parentElement, lang);
  const cur = node.nodeValue;
  const rec = orig.get(node);
  const source = rec && cur === rec.out ? rec.src : cur;
  const t = lookup(source, lang);
  const out = t == null ? source : wrap(source, t);
  orig.set(node, { src: source, out });
  if (out !== cur) node.nodeValue = out;
}

function doAttrs(el, lang) {
  if (skipped(el)) return;
  lang = effLang(el, lang);
  let m = attrOrig.get(el);
  if (!m) { m = {}; attrOrig.set(el, m); }
  for (const a of ATTRS) {
    const cur = el.getAttribute(a);
    if (cur == null) continue;
    const rec = m[a];
    const source = rec && cur === rec.out ? rec.src : cur;
    const t = lookup(source, lang);
    const out = t == null ? source : t;
    m[a] = { src: source, out };
    if (out !== cur) el.setAttribute(a, out);
  }
}

function walk(root, lang) {
  if (root.nodeType === 3) return doText(root, lang);
  if (root.nodeType !== 1 || skipped(root)) return;
  doAttrs(root, lang);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === 3) doText(n, lang);
    else doAttrs(n, lang);
    n = tw.nextNode();
  }
}

export function startDomTranslator(lang) {
  let busy = false;
  const run = (fn) => { busy = true; try { fn(); } finally { busy = false; } };
  run(() => walk(document.body, lang));
  const obs = new MutationObserver((muts) => {
    if (busy) return;
    run(() => {
      for (const m of muts) {
        if (m.type === "characterData") doText(m.target, lang);
        else if (m.type === "attributes") doAttrs(m.target, lang);
        else m.addedNodes.forEach((n) => walk(n, lang));
      }
    });
    obs.takeRecords();
  });
  obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  return () => obs.disconnect();
}
